// src/components/store/sections/product-cards/PastelSplitProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingBag, Clock, Flame, Heart, Package, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const SPLIT_THEMES = [
  {
    bgGradient: "from-[#dcfce7] via-[#f0fdf4] to-[#bbf7d0]",
    categoryColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    buttonColor: "bg-[#6366f1] hover:bg-[#4f46e5]",
  },
  {
    bgGradient: "from-[#240b36] via-[#1a0826] to-[#0c0213]",
    categoryColor: "bg-purple-50 text-purple-700 border-purple-200",
    buttonColor: "bg-[#ec4899] hover:bg-[#db2777]",
  },
  {
    bgGradient: "from-[#ecfccb] via-[#f7fee7] to-[#d9f99d]",
    categoryColor: "bg-lime-50 text-lime-800 border-lime-200",
    buttonColor: "bg-[#059669] hover:bg-[#047857]",
  },
  {
    bgGradient: "from-[#0a122e] via-[#05091a] to-[#020512]",
    categoryColor: "bg-blue-50 text-blue-700 border-blue-200",
    buttonColor: "bg-[#4338ca] hover:bg-[#3730a3]",
  },
];

export default function PastelSplitProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    stockCount,
    currentPrice,
    comparePrice,
    discountPercent,
    added,
    wishlisted,
    setWishlisted,
    timer,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const theme = SPLIT_THEMES[index % SPLIT_THEMES.length];

  return (
    <div className="bg-white rounded-[28px] p-4 shadow-md border border-slate-100 flex flex-col justify-between h-full relative group hover:shadow-xl transition-all duration-300 select-none">
      <div>
        <div
          className={`relative w-full h-44 rounded-[22px] bg-gradient-to-b ${theme.bgGradient} overflow-hidden flex items-center justify-center p-3 mb-3`}
        >
          <span className="absolute top-2.5 left-2.5 z-20 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-rose-500 text-white font-black text-[10px] shadow-xs">
            <Flame className="w-3 h-3 fill-white" />
            <span>{discountPercent}% OFF</span>
          </span>
          <button
            type="button"
            onClick={() => setWishlisted(!wishlisted)}
            className="absolute top-2.5 right-2.5 z-20 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-slate-500 hover:text-rose-500 cursor-pointer"
          >
            <Heart className={`w-3.5 h-3.5 ${wishlisted ? "fill-rose-500 text-rose-500" : ""}`} />
          </button>

          <Link href={productUrl} className="relative w-32 h-32 z-10 block">
            <Image
              src={productImg}
              alt={productName}
              fill
              unoptimized
              className="object-contain group-hover:scale-105 transition-transform duration-500 drop-shadow-lg"
            />
          </Link>
        </div>

        <div className="flex items-center justify-between gap-2 mb-1">
          <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-bold border truncate ${theme.categoryColor}`}>
            {categoryName}
          </span>
          <span className="flex items-center gap-0.5 text-[11px] font-bold text-amber-500">
            <Star className="w-3 h-3 fill-amber-400" /> {rating.toFixed(1)}
          </span>
        </div>

        <Link
          href={productUrl}
          className="text-sm font-black text-slate-900 hover:text-purple-600 transition-colors line-clamp-1 block"
        >
          {productName}
        </Link>

        <div className="flex items-baseline gap-2 mt-1.5 font-mono">
          <span className="text-lg font-black text-purple-600">৳{currentPrice.toLocaleString()}</span>
          {comparePrice > currentPrice && (
            <span className="text-xs text-slate-400 line-through">৳{comparePrice.toLocaleString()}</span>
          )}
        </div>
      </div>

      <div className="space-y-2.5 mt-3 pt-2.5 border-t border-slate-100">
        <div className="bg-slate-50 rounded-xl p-2 border border-slate-100 flex items-center justify-between text-[10px] font-mono font-bold">
          <span className="text-rose-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Ends in:
          </span>
          <span className="text-slate-800">
            {String(timer.hours).padStart(2, "0")}h:{String(timer.minutes).padStart(2, "0")}m:
            <span className="text-rose-600">{String(timer.seconds).padStart(2, "0")}s</span>
          </span>
        </div>

        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1">
            <Package className="w-3.5 h-3.5 text-purple-600" />
            Stock: <strong className="text-emerald-600">{stockCount}</strong>
          </span>
          <button
            type="button"
            onClick={(e) => handleAddToCart(e)}
            className={`px-4 py-2 rounded-full ${theme.buttonColor} text-white font-black text-[10px] uppercase shadow-sm flex items-center gap-1 active:scale-95 transition-all cursor-pointer`}
          >
            {added ? <Check className="w-3 h-3" /> : <ShoppingBag className="w-3 h-3" />}
            <span>{added ? "Added" : "Shop Deal"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
